import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Globe, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SUPPORTED, LANG_NAMES } from "../i18n";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Accroche affichée DANS la langue proposée (plus convaincant pour le visiteur).
const PROMPT = {
  fr: "Afficher le site en Français ?",
  en: "View this site in English?",
  es: "¿Ver el sitio en Español?",
  de: "Diese Seite auf Deutsch ansehen?",
  pt: "Ver o site em Português?",
  "zh-CN": "以简体中文浏览本网站？",
};
const YES = { fr: "Oui", en: "Yes", es: "Sí", de: "Ja", pt: "Sim", "zh-CN": "好的" };
const NO = { fr: "Non merci", en: "No thanks", es: "No, gracias", de: "Nein danke", pt: "Não, obrigado", "zh-CN": "不用了" };

export default function GeoLangBanner() {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const [suggest, setSuggest] = useState(null);

  useEffect(() => {
    // Non intrusif : jamais si l'utilisateur a déjà choisi manuellement, ni déjà vu la bannière.
    if (localStorage.getItem("om_lang_manual") === "1") return;
    if (sessionStorage.getItem("om_geo_seen") === "1") return;
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BACKEND}/api/i18n/geo`);
        if (!r.ok) return;
        const data = await r.json();
        const lang = data?.lang;
        const current = SUPPORTED.includes(i18n.language) ? i18n.language : "fr";
        if (alive && lang && SUPPORTED.includes(lang) && lang !== current) {
          setSuggest(lang);
        }
      } catch (_) {}
    })();
    return () => { alive = false; };
  }, []); // eslint-disable-line

  if (!suggest) return null;
  // Masquée sur le back-office / l'espace membre (non-public).
  if (pathname.startsWith("/admin") || pathname.startsWith("/mon-compte")) return null;

  const dismiss = () => { sessionStorage.setItem("om_geo_seen", "1"); setSuggest(null); };
  const accept = () => {
    localStorage.setItem("om_lang_manual", "1");
    sessionStorage.setItem("om_geo_seen", "1");
    try { i18n.changeLanguage(suggest); } catch (_) {}
    setSuggest(null);
  };

  return (
    <div
      data-testid="geo-lang-banner"
      className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[90] w-[calc(100%-2rem)] max-w-md
                 bg-[#0d0d10] border border-[#C19A6B]/40 rounded-[10px] shadow-2xl
                 px-4 py-3 flex items-center gap-3 animate-in fade-in slide-in-from-bottom-4"
    >
      <div className="flex items-center justify-center w-9 h-9 rounded-full bg-[#C19A6B]/15 shrink-0">
        <Globe className="w-5 h-5 text-[#C19A6B]" />
      </div>
      <p className="flex-1 text-sm text-white leading-snug">
        {PROMPT[suggest] || `View this site in ${LANG_NAMES[suggest]}?`}
      </p>
      <button
        data-testid="geo-lang-accept"
        onClick={accept}
        className="text-xs font-medium px-3 py-1.5 rounded-[6px] bg-[#C19A6B] text-white hover:bg-[#a88358] transition-colors whitespace-nowrap"
      >
        {YES[suggest] || "Yes"}
      </button>
      <button
        data-testid="geo-lang-dismiss"
        onClick={dismiss}
        aria-label={NO[suggest] || "No thanks"}
        className="text-white/50 hover:text-white transition-colors shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
